import React, { useEffect, useState, useRef } from 'react';
import { GripVerticalIcon, MaximizeIcon, MinimizeIcon, XIcon } from 'lucide-react';
interface DraggableModalProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  defaultWidth?: number;
  defaultHeight?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}
export function DraggableModal({
  children,
  isOpen,
  onClose,
  title,
  defaultWidth = 800,
  defaultHeight = 600,
  minWidth = 400,
  minHeight = 300,
  maxWidth = window.innerWidth - 40,
  maxHeight = window.innerHeight - 40
}: DraggableModalProps) {
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [size, setSize] = useState({
    width: defaultWidth,
    height: defaultHeight
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string>('');
  const [isMaximized, setIsMaximized] = useState(false);
  const [preMaximizeState, setPreMaximizeState] = useState<{
    position: {
      x: number;
      y: number;
    };
    size: {
      width: number;
      height: number;
    };
  } | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const dragStartPos = useRef({
    x: 0,
    y: 0
  });
  const resizeStartPos = useRef({
    x: 0,
    y: 0
  });
  const resizeStartSize = useRef({
    width: 0,
    height: 0
  });
  // Center modal on mount
  useEffect(() => {
    if (isOpen) {
      const centerX = (window.innerWidth - defaultWidth) / 2;
      const centerY = (window.innerHeight - defaultHeight) / 2;
      setPosition({
        x: centerX,
        y: centerY
      });
    }
  }, [isOpen, defaultWidth, defaultHeight]);
  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isMaximized) return;
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };
  // Handle resizing
  const handleResizeMouseDown = (e: React.MouseEvent, direction: string) => {
    if (isMaximized) return;
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStartPos.current = {
      x: e.clientX,
      y: e.clientY
    };
    resizeStartSize.current = {
      ...size
    };
  };
  // Handle maximize/restore
  const handleToggleMaximize = () => {
    if (isMaximized) {
      // Restore
      if (preMaximizeState) {
        setPosition(preMaximizeState.position);
        setSize(preMaximizeState.size);
      }
      setIsMaximized(false);
    } else {
      // Maximize
      setPreMaximizeState({
        position: {
          ...position
        },
        size: {
          ...size
        }
      });
      setPosition({
        x: 20,
        y: 20
      });
      setSize({
        width: window.innerWidth - 40,
        height: window.innerHeight - 40
      });
      setIsMaximized(true);
    }
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragStartPos.current.x;
        const newY = e.clientY - dragStartPos.current.y;
        // Constrain to viewport
        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
      if (isResizing) {
        const deltaX = e.clientX - resizeStartPos.current.x;
        const deltaY = e.clientY - resizeStartPos.current.y;
        let newWidth = resizeStartSize.current.width;
        let newHeight = resizeStartSize.current.height;
        let newX = position.x;
        let newY = position.y;
        if (resizeDirection.includes('e')) {
          newWidth = Math.max(minWidth, Math.min(maxWidth, resizeStartSize.current.width + deltaX));
        }
        if (resizeDirection.includes('s')) {
          newHeight = Math.max(minHeight, Math.min(maxHeight, resizeStartSize.current.height + deltaY));
        }
        if (resizeDirection.includes('w')) {
          const potentialWidth = resizeStartSize.current.width - deltaX;
          if (potentialWidth >= minWidth && potentialWidth <= maxWidth) {
            newWidth = potentialWidth;
            newX = position.x + deltaX;
          }
        }
        if (resizeDirection.includes('n')) {
          const potentialHeight = resizeStartSize.current.height - deltaY;
          if (potentialHeight >= minHeight && potentialHeight <= maxHeight) {
            newHeight = potentialHeight;
            newY = position.y + deltaY;
          }
        }
        setSize({
          width: newWidth,
          height: newHeight
        });
        setPosition({
          x: newX,
          y: newY
        });
      }
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeDirection('');
    };
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isDragging ? 'move' : 'nwse-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, isResizing, position, size, resizeDirection, minWidth, minHeight, maxWidth, maxHeight]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} data-id="element-1998">
      <div ref={modalRef} className="absolute bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden" style={{
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: `${size.width}px`,
      height: `${size.height}px`
    }} onClick={e => e.stopPropagation()} data-id="element-1999">
        {/* Draggable Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-b border-gray-200 cursor-move select-none" onMouseDown={handleMouseDown} data-id="element-2000">
          <div className="flex items-center gap-2" data-id="element-2001">
            <GripVerticalIcon className="w-4 h-4 text-gray-400" data-id="element-2002" />
            {title && <span className="text-sm font-semibold text-gray-900" data-id="element-2003">
                {title}
              </span>}
          </div>
          <div className="flex items-center gap-1" data-id="element-2004">
            <button onClick={handleToggleMaximize} className="p-1 hover:bg-gray-200 rounded transition-colors" title={isMaximized ? 'Restore' : 'Maximize'} data-id="element-2005">
              {isMaximized ? <MinimizeIcon className="w-4 h-4 text-gray-600" data-id="element-2006" /> : <MaximizeIcon className="w-4 h-4 text-gray-600" data-id="element-2007" />}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-gray-200 rounded transition-colors" title="Close" data-id="element-2008">
              <XIcon className="w-4 h-4 text-gray-600" data-id="element-2009" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden" data-id="element-2010">{children}</div>

        {/* Resize Handles */}
        {!isMaximized && <>
            {/* Corners */}
            <div className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize" onMouseDown={e => handleResizeMouseDown(e, 'nw')} data-id="element-2011" />
            <div className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize" onMouseDown={e => handleResizeMouseDown(e, 'ne')} data-id="element-2012" />
            <div className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize" onMouseDown={e => handleResizeMouseDown(e, 'sw')} data-id="element-2013" />
            <div className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize" onMouseDown={e => handleResizeMouseDown(e, 'se')} data-id="element-2014" />

            {/* Edges */}
            <div className="absolute top-0 left-3 right-3 h-1 cursor-n-resize" onMouseDown={e => handleResizeMouseDown(e, 'n')} data-id="element-2015" />
            <div className="absolute bottom-0 left-3 right-3 h-1 cursor-s-resize" onMouseDown={e => handleResizeMouseDown(e, 's')} data-id="element-2016" />
            <div className="absolute left-0 top-3 bottom-3 w-1 cursor-w-resize" onMouseDown={e => handleResizeMouseDown(e, 'w')} data-id="element-2017" />
            <div className="absolute right-0 top-3 bottom-3 w-1 cursor-e-resize" onMouseDown={e => handleResizeMouseDown(e, 'e')} data-id="element-2018" />
          </>}
      </div>
    </div>;
}